import { FileText } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ImageLightbox } from '@/components/shared/image-lightbox';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { SupportTicketAttachment } from '@/pages/support/types';
import { cn } from '@/utils/cn';

const isImage = (file: SupportTicketAttachment) => file.kind === 'image';
const isPdf = (file: SupportTicketAttachment) =>
  file.contentType === 'application/pdf' || file.fileName.toLowerCase().endsWith('.pdf');

export const SupportTicketAttachments = ({
  attachments,
  className,
}: {
  attachments: SupportTicketAttachment[];
  className?: string;
}) => {
  const { t } = useTranslation('support');
  const { t: tCommon } = useTranslation();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [pdfId, setPdfId] = useState<string | null>(null);

  const images = useMemo(() => attachments.filter((file) => isImage(file) && file.downloadUrl), [
    attachments,
  ]);
  const lightboxImages = useMemo(
    () => images.map((file) => file.downloadUrl ?? ''),
    [images],
  );
  const pdfFile =
    attachments.find((file) => file.id === pdfId && isPdf(file) && file.downloadUrl) ?? null;

  if (attachments.length === 0) return null;

  const documents = attachments.filter((file) => !isImage(file));

  return (
    <div className={cn('mt-3 flex flex-col gap-3', className)}>
      {images.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {images.map((file, index) => (
            <li key={file.id}>
              <AttachmentImageTile
                file={file}
                label={t('attachments.preview', { fileName: file.fileName })}
                onOpen={() => setLightboxIndex(index)}
              />
            </li>
          ))}
        </ul>
      ) : null}
      {documents.length > 0 ? (
        <ul className="flex flex-col gap-1.5">
          {documents.map((file) => (
            <li key={file.id}>
              <button
                type="button"
                className="flex min-h-11 w-full items-center gap-2 rounded-md border border-border px-3 py-2 text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                disabled={!file.downloadUrl || !isPdf(file)}
                onClick={() => setPdfId(file.id)}
              >
                <FileText aria-hidden className="size-4 shrink-0 text-fg-tertiary" />
                <span className="min-w-0 truncate text-body-sm text-fg">{file.fileName}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <ImageLightbox
        images={lightboxImages}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
        labels={{
          dialogLabel: tCommon('lightbox.title'),
          close: tCommon('lightbox.close'),
          previous: tCommon('lightbox.previous'),
          next: tCommon('lightbox.next'),
          count:
            lightboxIndex !== null
              ? tCommon('lightbox.imageCount', {
                  current: lightboxIndex + 1,
                  total: lightboxImages.length,
                })
              : '',
          imageAlt:
            lightboxIndex !== null && images[lightboxIndex]
              ? images[lightboxIndex].fileName
              : '',
        }}
      />

      {pdfFile?.downloadUrl ? (
        <Dialog open onOpenChange={(open) => { if (!open) setPdfId(null); }}>
          <DialogContent size="lg" closeLabel={tCommon('action.close')}>
            <DialogHeader>
              <DialogTitle>{pdfFile.fileName}</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <iframe
                title={pdfFile.fileName}
                src={pdfFile.downloadUrl}
                className="h-[min(60dvh,32rem)] w-full rounded-lg border border-border bg-surface"
              />
            </DialogBody>
            <DialogFooter>
              <Button asChild>
                <a href={pdfFile.downloadUrl} target="_blank" rel="noopener noreferrer">
                  {t('attachments.openPdf')}
                </a>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
};

const AttachmentImageTile = ({
  file,
  label,
  onOpen,
}: {
  file: SupportTicketAttachment;
  label: string;
  onOpen: () => void;
}) => {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(file.downloadUrl) && !failed;

  return (
    <button
      type="button"
      className="block size-20 cursor-zoom-in overflow-hidden rounded-lg bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
      aria-label={label}
      disabled={!showImage}
      onClick={onOpen}
    >
      {showImage ? (
        <img
          src={file.downloadUrl ?? undefined}
          alt=""
          className="size-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="flex size-full items-center justify-center p-2 text-center text-caption text-fg-secondary">
          {file.fileName}
        </span>
      )}
    </button>
  );
};
